import en from "@/i18n/locales/en.json";
import type { SeoLanguage } from "@/lib/seoMetadata";

type UnknownRecord = Record<string, unknown>;

export type LocalizedFaqTemplateSet = {
  questions: string[];
  answers: string[];
  companyClauses: string[];
};

/**
 * The three blog FAQ archetypes, as template keys.
 *
 * `resolveBlogFaqArchetype` in :mod:`faqResolver` picks one per post from its
 * tags, category and slug. That choice is code — it reads English keywords in
 * repository data — while the copy it selects is not, which is the split
 * CWS-115 drew when it moved 240 literals out of the resolver.
 */
export const BLOG_ARCHETYPE_TEMPLATE_KEYS = [
  "blogTechnical",
  "blogCompanyNews",
  "blogStrategy",
] as const;

/**
 * Topic families the technical gain sentence branches over.
 *
 * ⚠️ **Not the same four keys as {@link TECHNICAL_RISK_KEYS}, deliberately.**
 * The two maps sit next to each other in the locale files and look parallel,
 * but gain branches on the post's *subject matter* — `coding` is a gain family
 * and not a risk profile — while risk branches on its *failure mode*, where
 * `evaluation` is a profile and not a gain family. The keyword groups behind
 * them differ too. A locale file that keys one map like the other loses the
 * copy for two branches; `faqLocaleShape.test.ts` compares both key sets
 * across the five files for that reason.
 */
export const TECHNICAL_GAIN_KEYS = ["inference", "coding", "training", "general"] as const;

/**
 * Risk profiles `resolveTechnicalRiskProfile` returns.
 *
 * See the warning on {@link TECHNICAL_GAIN_KEYS}: three of these four names are
 * shared with it and one differs in each direction.
 */
export const TECHNICAL_RISK_KEYS = [
  "inference",
  "training",
  "evaluation",
  "general",
] as const;

/** One of the three blog archetype template keys. */
export type BlogArchetypeTemplateKey = (typeof BLOG_ARCHETYPE_TEMPLATE_KEYS)[number];

/** One of the four technical gain topic families. */
export type TechnicalGainKey = (typeof TECHNICAL_GAIN_KEYS)[number];

/** One of the four technical risk profiles. */
export type TechnicalRiskKey = (typeof TECHNICAL_RISK_KEYS)[number];

/**
 * The blog answer sentences the resolver selects rather than always renders.
 *
 * Kept beside `templates` rather than inside it because a
 * :type:`LocalizedFaqTemplates` member is a question/answer/clause triple, and
 * more than one walker in the test suite relies on that being true of every
 * member. In the locale files these live under `faqs.templates.blogVariants`,
 * so the JSON subtree stays "all FAQ template copy" — the invariant
 * `faqLocaleShape.test.ts` pins.
 */
export type LocalizedBlogVariants = {
  companyNewsLead: string;
  technicalGain: Record<TechnicalGainKey, { withMetrics: string; withoutMetrics: string }>;
  technicalRisk: Record<TechnicalRiskKey, string>;
};

export type LocalizedFaqTemplates = {
  home: LocalizedFaqTemplateSet;
  static: LocalizedFaqTemplateSet;
  servicesConsulting: LocalizedFaqTemplateSet;
  servicesFixedPrice: LocalizedFaqTemplateSet;
  servicesTeams: LocalizedFaqTemplateSet;
  servicesEnablement: LocalizedFaqTemplateSet;
  openSourceKindlyWebSearch: LocalizedFaqTemplateSet;
  openSourceLadCodeReview: LocalizedFaqTemplateSet;
  openSourceTdd: LocalizedFaqTemplateSet;
  sovereignAi: LocalizedFaqTemplateSet;
  caseStudiesIndex: LocalizedFaqTemplateSet;
  blogIndex: LocalizedFaqTemplateSet;
  insightsIndex: LocalizedFaqTemplateSet;
  industriesDefence: LocalizedFaqTemplateSet;
  industriesPharma: LocalizedFaqTemplateSet;
  industriesFinance: LocalizedFaqTemplateSet;
  industriesEnterprise: LocalizedFaqTemplateSet;
  terms: LocalizedFaqTemplateSet;
  privacy: LocalizedFaqTemplateSet;
  blogTechnical: LocalizedFaqTemplateSet;
  blogCompanyNews: LocalizedFaqTemplateSet;
  blogStrategy: LocalizedFaqTemplateSet;
  caseStudy: LocalizedFaqTemplateSet;
};

export type LocalizedFaqSource = {
  lang: SeoLanguage;
  routes: Record<string, string>;
  templates: LocalizedFaqTemplates;
  blogVariants: LocalizedBlogVariants;
  pageMeta: Record<string, { title: string; description: string }>;
};

/**
 * The registry of locale payloads the FAQ resolver reads.
 *
 * Since CWS-199 only ``en`` — the fallback language — is statically bundled;
 * the other four register here as their chunks arrive through
 * {@link registerFaqLocale}, loaded by ``src/i18n``'s dynamic loader. The
 * active locale is always registered before the first render (`main.tsx`
 * awaits `initializeLanguage`), so the synchronous readers below never see a
 * missing production slot; ``getLocalizedFaqSource`` returns ``null`` for an
 * unregistered language, the same shape its parse-failure path always had.
 */
const LOCALE_FAQ_PAYLOADS_SEED: Partial<Record<SeoLanguage, unknown>> = {
  en,
};

let LOCALE_FAQ_PAYLOADS: Partial<Record<SeoLanguage, unknown>> = {
  ...LOCALE_FAQ_PAYLOADS_SEED,
};

/**
 * Restore the registry to its module-init state (``en`` only)
 *
 * Test-only, like ``src/i18n``'s ``resetLocaleRegistrationForTests`` (which
 * calls this): the lazy-loader suites must observe a failed load leave a
 * locale **unregistered**, which is only visible if earlier cases'
 * registrations can be forgotten. Production never calls it.
 */
export function resetFaqRegistryForTests(): void {
  LOCALE_FAQ_PAYLOADS = { ...LOCALE_FAQ_PAYLOADS_SEED };
}

/**
 * Register one locale's payload for the FAQ resolver.
 *
 * A no-op for an already-registered slot — idempotent by design, so the
 * loader's registration and the module-init seeding of ``en`` can both run
 * without ordering hazards. Exported for ``src/i18n``'s loader and re-exported
 * by ``faqResolver`` for `scripts/audit-faq-coverage.mjs`, which seeds the
 * registry through the same module instance it resolves FAQ sources from.
 *
 * Args:
 *     lang: The locale the payload belongs to.
 *     payload: The parsed locale JSON payload (UI copy, ``meta`` tree and
 *         ``faqs`` tree together).
 */
export function registerFaqLocale(lang: SeoLanguage, payload: unknown): void {
  if (LOCALE_FAQ_PAYLOADS[lang] !== undefined) {
    return;
  }
  LOCALE_FAQ_PAYLOADS[lang] = payload;
}

export function getLocalizedFaqSource(lang: SeoLanguage): LocalizedFaqSource | null {
  const payload = LOCALE_FAQ_PAYLOADS[lang];
  if (payload === undefined) {
    return null;
  }
  return parseLocaleFaqSource(payload, lang);
}

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

/**
 * Narrow a value to a string with visible content.
 *
 * The ``value is string`` predicate is load-bearing rather than decorative:
 * without it every ``const { title } = pageValue`` below stayed ``unknown``
 * after the guard, which is what produced the four ``TS2322`` errors this file
 * carried until CWS-115 (`TEST_SUITE.md` T23).
 *
 * @param value - Any parsed JSON value.
 * @returns ``true`` when the value is a string that is not blank.
 */
function hasNonEmptyString(value: unknown): value is string {
  return isString(value) && value.trim().length > 0;
}

function isNonEmptyStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.length > 0 && value.every(hasNonEmptyString);
}

function parseTemplateSet(value: unknown): LocalizedFaqTemplateSet | null {
  if (!isRecord(value)) {
    return null;
  }

  const questions = value.questions;
  const answers = value.answers;
  const companyClauses = value.companyClauses;

  if (
    !isNonEmptyStringArray(questions) ||
    !isNonEmptyStringArray(answers) ||
    !isNonEmptyStringArray(companyClauses)
  ) {
    return null;
  }

  if (
    questions.length !== answers.length ||
    answers.length !== companyClauses.length
  ) {
    return null;
  }

  return {
    questions,
    answers,
    companyClauses,
  };
}

function hasTemplateToken(value: string, token: string) {
  return value.includes(token);
}

/**
 * Check that a record carries exactly the expected keys and no others.
 *
 * @param value - The record to inspect.
 * @param expected - The keys it must carry, in any order.
 * @returns ``true`` when the two key sets are equal.
 */
function hasExactKeys(value: UnknownRecord, expected: readonly string[]) {
  const actual = Object.keys(value);
  const allowed = new Set<string>(expected);
  return actual.length === allowed.size && actual.every((key) => allowed.has(key));
}

function validateTemplateTokens(templates: LocalizedFaqTemplates) {
  const serviceTemplates = [
    templates.servicesConsulting,
    templates.servicesFixedPrice,
    templates.servicesTeams,
    templates.servicesEnablement,
  ];
  const serviceTokensValid = serviceTemplates.every(
    (template) => template.answers.some((value) => hasTemplateToken(value, "{pageDescription}")),
  );

  if (!serviceTokensValid) {
    return false;
  }

  const openSourceTemplates = [
    templates.openSourceKindlyWebSearch,
    templates.openSourceLadCodeReview,
    templates.openSourceTdd,
  ];
  const openSourceTokensValid = openSourceTemplates.every(
    (template) => template.answers.some((value) => hasTemplateToken(value, "{pageDescription}")),
  );

  if (!openSourceTokensValid) {
    return false;
  }

  const sovereignAiTokensValid = templates.sovereignAi.answers.some((value) =>
    hasTemplateToken(value, "{pageDescription}"),
  );

  if (!sovereignAiTokensValid) {
    return false;
  }

  const caseStudiesIndexTokensValid = templates.caseStudiesIndex.answers.some((value) =>
    hasTemplateToken(value, "{pageDescription}"),
  );

  if (!caseStudiesIndexTokensValid) {
    return false;
  }

  // `blogIndex` is deliberately absent. It required `{pageDescription}` until
  // CWS-45 dropped the description from `blogIndex.answers[3]`, its only
  // interpolation, because the site-level blog snippet restated answer 0 rather
  // than answering its own question (`SYSTEM_DESIGN.md` §16.1b).

  const insightsIndexTokensValid = templates.insightsIndex.answers.some((value) =>
    hasTemplateToken(value, "{pageDescription}"),
  );

  if (!insightsIndexTokensValid) {
    return false;
  }

  const industryTemplates = [
    templates.industriesDefence,
    templates.industriesPharma,
    templates.industriesFinance,
    templates.industriesEnterprise,
  ];
  const industryTokensValid = industryTemplates.every(
    (template) => template.answers.some((value) => hasTemplateToken(value, "{pageDescription}")),
  );

  if (!industryTokensValid) {
    return false;
  }

  const homeAnswers = templates.home.answers;
  const homeTokensValid = homeAnswers.some((value) => hasTemplateToken(value, "{pageDescription}"));

  if (!homeTokensValid) {
    return false;
  }

  const staticQuestions = templates.static.questions;
  const staticAnswers = templates.static.answers;
  const staticTokensValid =
    staticQuestions.some((value) => hasTemplateToken(value, "{topic}")) &&
    staticAnswers.some((value) => hasTemplateToken(value, "{pageDescription}"));

  if (!staticTokensValid) {
    return false;
  }

  // Every blog archetype interpolates the post's tags, and each one leads with
  // a sentence of its own: the summary for two of them, the company-news lead
  // for the third. A template that drops its token renders an answer with a
  // hole in it on every blog route in that locale (CWS-115).
  const blogTokensValid = BLOG_ARCHETYPE_TEMPLATE_KEYS.every((key) =>
    templates[key].answers.some((value) => hasTemplateToken(value, "{tags}")),
  );

  if (!blogTokensValid) {
    return false;
  }

  const blogLeadTokensValid =
    templates.blogTechnical.answers.some((value) => hasTemplateToken(value, "{summary}")) &&
    templates.blogStrategy.answers.some((value) => hasTemplateToken(value, "{summary}")) &&
    templates.blogCompanyNews.answers.some((value) =>
      hasTemplateToken(value, "{companyNewsLead}"),
    );

  if (!blogLeadTokensValid) {
    return false;
  }

  const technicalVariantTokensValid =
    templates.blogTechnical.answers.some((value) => hasTemplateToken(value, "{technicalGain}")) &&
    templates.blogTechnical.answers.some((value) => hasTemplateToken(value, "{technicalRisk}"));

  if (!technicalVariantTokensValid) {
    return false;
  }

  if (!templates.caseStudy.questions.some((value) => hasTemplateToken(value, "{client}"))) {
    return false;
  }

  if (!templates.caseStudy.answers.some((value) => hasTemplateToken(value, "{client}"))) {
    return false;
  }

  const caseStudyDeliveryAnswer = templates.caseStudy.answers[1];
  if (!caseStudyDeliveryAnswer || !hasTemplateToken(caseStudyDeliveryAnswer, "{services}")) {
    return false;
  }

  return true;
}

/**
 * Read the blog answer sentences the resolver selects between.
 *
 * Rejects a partial map rather than filling a default, for the reason every
 * other parser here does: a missing sentence would otherwise render as an empty
 * string inside an answer, which reads as a copy accident on a live page rather
 * than as a configuration error at load.
 *
 * @param value - The `faqs.templates.blogVariants` node of a locale payload.
 * @returns The parsed variants, or ``null`` when any is missing or empty.
 */
function parseBlogVariants(value: unknown): LocalizedBlogVariants | null {
  if (!isRecord(value)) {
    return null;
  }

  const { companyNewsLead, technicalGain, technicalRisk } = value;
  if (!hasNonEmptyString(companyNewsLead) || !hasTemplateToken(companyNewsLead, "{topic}")) {
    return null;
  }

  if (!isRecord(technicalGain) || !isRecord(technicalRisk)) {
    return null;
  }

  const gain = {} as LocalizedBlogVariants["technicalGain"];
  for (const key of TECHNICAL_GAIN_KEYS) {
    const entry = technicalGain[key];
    if (!isRecord(entry)) {
      return null;
    }

    const { withMetrics, withoutMetrics } = entry;
    // `withMetrics` exists only to place the extracted metric claims; without
    // the token it is a silent duplicate of the sentence below it. The reverse
    // matters too: the token in `withoutMetrics` renders a dangling clause,
    // since that branch is chosen precisely when there is nothing to place.
    if (
      !hasNonEmptyString(withMetrics) ||
      !hasTemplateToken(withMetrics, "{metricsPhrase}") ||
      !hasNonEmptyString(withoutMetrics) ||
      hasTemplateToken(withoutMetrics, "{metricsPhrase}")
    ) {
      return null;
    }

    gain[key] = { withMetrics, withoutMetrics };
  }

  // Key sets are compared, not merely sampled: reading only the four known keys
  // would let a typo such as `infernce` sit alongside them unnoticed in one
  // locale, where it is copy nobody renders and nobody can find.
  if (!hasExactKeys(technicalGain, TECHNICAL_GAIN_KEYS)) {
    return null;
  }

  const risk = {} as LocalizedBlogVariants["technicalRisk"];
  for (const key of TECHNICAL_RISK_KEYS) {
    const entry = technicalRisk[key];
    if (!hasNonEmptyString(entry)) {
      return null;
    }

    risk[key] = entry;
  }

  if (!hasExactKeys(technicalRisk, TECHNICAL_RISK_KEYS)) {
    return null;
  }

  return {
    companyNewsLead,
    technicalGain: gain,
    technicalRisk: risk,
  };
}

function parsePageMeta(payload: unknown): Record<string, { title: string; description: string }> | null {
  if (!isRecord(payload)) {
    return null;
  }

  const meta = payload.meta;
  if (!isRecord(meta)) {
    return null;
  }

  const homeTitle = meta.homeTitle;
  const homeDescription = meta.homeDescription;
  if (!hasNonEmptyString(homeTitle) || !hasNonEmptyString(homeDescription)) {
    return null;
  }

  const pagesRaw = meta.pages;
  if (!isRecord(pagesRaw)) {
    return null;
  }

  const pageMeta: Record<string, { title: string; description: string }> = {
    "": {
      title: homeTitle,
      description: homeDescription,
    },
  };

  for (const [routePath, pageValue] of Object.entries(pagesRaw)) {
    if (!isRecord(pageValue)) {
      return null;
    }

    const title = pageValue.title;
    const description = pageValue.description;
    if (!hasNonEmptyString(title) || !hasNonEmptyString(description)) {
      return null;
    }

    pageMeta[routePath] = { title, description };
  }

  return pageMeta;
}

function normalizeRoutes(value: unknown): Record<string, string> | null {
  if (!isRecord(value)) {
    return null;
  }

  const routes: Record<string, string> = {};

  for (const [key, routeValue] of Object.entries(value)) {
    if (!isString(routeValue)) {
      return null;
    }

    routes[key] = routeValue;
  }

  return routes;
}

/**
 * Validate one locale payload and project it onto the FAQ source shape.
 *
 * Exported for `faqLocalizedStore.test.ts`, which feeds it a clone of the
 * shipped payload with exactly one key broken. Every rejection path in this
 * module is otherwise unreachable from a test — the five payloads it reads are
 * module-level imports — so the only behaviour proven would be the happy one.
 *
 * @param payload - A parsed locale JSON payload.
 * @param lang - The locale the payload belongs to, recorded on the result.
 * @returns The FAQ source, or ``null`` when anything required is missing,
 *     empty, or has lost a token the resolver interpolates.
 */
export function parseLocaleFaqSource(payload: unknown, lang: SeoLanguage): LocalizedFaqSource | null {
  if (!isRecord(payload)) {
    return null;
  }

  const faqs = payload.faqs;
  if (!isRecord(faqs)) {
    return null;
  }

  const routes = normalizeRoutes(faqs.routes);
  if (!routes) {
    return null;
  }

  const templatesRaw = faqs.templates;
  if (!isRecord(templatesRaw)) {
    return null;
  }

  const homeTemplates = templatesRaw.home;
  const staticTemplates = templatesRaw.static;
  const servicesConsultingTemplates = templatesRaw.servicesConsulting;
  const servicesFixedPriceTemplates = templatesRaw.servicesFixedPrice;
  const servicesTeamsTemplates = templatesRaw.servicesTeams;
  const servicesEnablementTemplates = templatesRaw.servicesEnablement;
  const openSourceKindlyWebSearchTemplates = templatesRaw.openSourceKindlyWebSearch;
  const openSourceLadCodeReviewTemplates = templatesRaw.openSourceLadCodeReview;
  const openSourceTddTemplates = templatesRaw.openSourceTdd;
  const sovereignAiTemplates = templatesRaw.sovereignAi;
  const caseStudiesIndexTemplates = templatesRaw.caseStudiesIndex;
  const blogIndexTemplates = templatesRaw.blogIndex;
  const insightsIndexTemplates = templatesRaw.insightsIndex;
  const industriesDefenceTemplates = templatesRaw.industriesDefence;
  const industriesPharmaTemplates = templatesRaw.industriesPharma;
  const industriesFinanceTemplates = templatesRaw.industriesFinance;
  const industriesEnterpriseTemplates = templatesRaw.industriesEnterprise;
  const termsTemplates = templatesRaw.terms;
  const privacyTemplates = templatesRaw.privacy;
  const caseStudyTemplates = templatesRaw.caseStudy;

  const parsedHomeTemplates = parseTemplateSet(homeTemplates);
  const parsedStaticTemplates = parseTemplateSet(staticTemplates);
  const parsedServicesConsultingTemplates = parseTemplateSet(servicesConsultingTemplates);
  const parsedServicesFixedPriceTemplates = parseTemplateSet(servicesFixedPriceTemplates);
  const parsedServicesTeamsTemplates = parseTemplateSet(servicesTeamsTemplates);
  const parsedServicesEnablementTemplates = parseTemplateSet(servicesEnablementTemplates);
  const parsedOpenSourceKindlyWebSearchTemplates = parseTemplateSet(
    openSourceKindlyWebSearchTemplates,
  );
  const parsedOpenSourceLadCodeReviewTemplates = parseTemplateSet(
    openSourceLadCodeReviewTemplates,
  );
  const parsedOpenSourceTddTemplates = parseTemplateSet(openSourceTddTemplates);
  const parsedSovereignAiTemplates = parseTemplateSet(sovereignAiTemplates);
  const parsedCaseStudiesIndexTemplates = parseTemplateSet(caseStudiesIndexTemplates);
  const parsedBlogIndexTemplates = parseTemplateSet(blogIndexTemplates);
  const parsedInsightsIndexTemplates = parseTemplateSet(insightsIndexTemplates);
  const parsedIndustriesDefenceTemplates = parseTemplateSet(industriesDefenceTemplates);
  const parsedIndustriesPharmaTemplates = parseTemplateSet(industriesPharmaTemplates);
  const parsedIndustriesFinanceTemplates = parseTemplateSet(industriesFinanceTemplates);
  const parsedIndustriesEnterpriseTemplates = parseTemplateSet(industriesEnterpriseTemplates);
  const parsedTermsTemplates = parseTemplateSet(termsTemplates);
  const parsedPrivacyTemplates = parseTemplateSet(privacyTemplates);
  const parsedCaseStudyTemplates = parseTemplateSet(caseStudyTemplates);

  // The three archetypes are parsed through one loop rather than three named
  // constants: they differ only by key, and CWS-115 added them together.
  const parsedBlogTemplates: Partial<Record<BlogArchetypeTemplateKey, LocalizedFaqTemplateSet>> =
    {};
  for (const key of BLOG_ARCHETYPE_TEMPLATE_KEYS) {
    const parsed = parseTemplateSet(templatesRaw[key]);
    if (!parsed) {
      return null;
    }

    parsedBlogTemplates[key] = parsed;
  }

  const parsedBlogVariants = parseBlogVariants(templatesRaw.blogVariants);
  if (!parsedBlogVariants) {
    return null;
  }

  if (
    !parsedHomeTemplates ||
    !parsedStaticTemplates ||
    !parsedServicesConsultingTemplates ||
    !parsedServicesFixedPriceTemplates ||
    !parsedServicesTeamsTemplates ||
    !parsedServicesEnablementTemplates ||
    !parsedOpenSourceKindlyWebSearchTemplates ||
    !parsedOpenSourceLadCodeReviewTemplates ||
    !parsedOpenSourceTddTemplates ||
    !parsedSovereignAiTemplates ||
    !parsedCaseStudiesIndexTemplates ||
    !parsedBlogIndexTemplates ||
    !parsedInsightsIndexTemplates ||
    !parsedIndustriesDefenceTemplates ||
    !parsedIndustriesPharmaTemplates ||
    !parsedIndustriesFinanceTemplates ||
    !parsedIndustriesEnterpriseTemplates ||
    !parsedTermsTemplates ||
    !parsedPrivacyTemplates ||
    !parsedCaseStudyTemplates
  ) {
    return null;
  }

  const templates: LocalizedFaqTemplates = {
    home: parsedHomeTemplates,
    static: parsedStaticTemplates,
    servicesConsulting: parsedServicesConsultingTemplates,
    servicesFixedPrice: parsedServicesFixedPriceTemplates,
    servicesTeams: parsedServicesTeamsTemplates,
    servicesEnablement: parsedServicesEnablementTemplates,
    openSourceKindlyWebSearch: parsedOpenSourceKindlyWebSearchTemplates,
    openSourceLadCodeReview: parsedOpenSourceLadCodeReviewTemplates,
    openSourceTdd: parsedOpenSourceTddTemplates,
    sovereignAi: parsedSovereignAiTemplates,
    caseStudiesIndex: parsedCaseStudiesIndexTemplates,
    blogIndex: parsedBlogIndexTemplates,
    insightsIndex: parsedInsightsIndexTemplates,
    industriesDefence: parsedIndustriesDefenceTemplates,
    industriesPharma: parsedIndustriesPharmaTemplates,
    industriesFinance: parsedIndustriesFinanceTemplates,
    industriesEnterprise: parsedIndustriesEnterpriseTemplates,
    terms: parsedTermsTemplates,
    privacy: parsedPrivacyTemplates,
    // Non-null asserted rather than left to `strictNullChecks: false`: the loop
    // above returns early on any missing archetype, and the four `TS2322`s this
    // file just lost were the same class of implicit widening.
    blogTechnical: parsedBlogTemplates.blogTechnical!,
    blogCompanyNews: parsedBlogTemplates.blogCompanyNews!,
    blogStrategy: parsedBlogTemplates.blogStrategy!,
    caseStudy: parsedCaseStudyTemplates,
  };

  if (!validateTemplateTokens(templates)) {
    return null;
  }

  const pageMeta = parsePageMeta(payload);
  if (!pageMeta) {
    return null;
  }

  return {
    lang,
    routes,
    templates,
    blogVariants: parsedBlogVariants,
    pageMeta,
  };
}
